import { useState, useEffect, useMemo } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  Plus,
  Trash2,
  Loader2,
  AlertCircle,
  Info,
  RefreshCw,
} from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { formatCurrency } from '@/lib/utils';
import {
  CREATE_INVOICE,
  APPROVE_INVOICE,
  GET_INVOICES_BY_ORGANIZATION,
  type Invoice,
} from '@/graphql/invoices';
import {
  GET_ORGANIZATION_PRICING,
  type GetOrganizationPricingResponse,
  type GetOrganizationPricingVariables,
} from '@/graphql/organizationPricing';

// ==================== Types ====================

interface LineItemRow {
  id: string; // temp id for UI
  productId: string;
  sku: string;
  description: string;
  unitPrice: number;
  priceSource: 'override' | 'catalog' | 'custom' | '';
  quantity: number;
  lineTotal: number;
}

interface CreateInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
}

// ==================== Helpers ====================

function todayISO(): string {
  return new Date().toISOString().split('T')[0];
}

function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
}

let tempIdCounter = 0;
function nextTempId(): string {
  return `temp-${++tempIdCounter}`;
}

// ==================== Component ====================

export function CreateInvoiceModal({ isOpen, onClose, clientId }: CreateInvoiceModalProps) {
  // Form state
  const [invoiceDate, setInvoiceDate] = useState(todayISO());
  const [dueDate, setDueDate] = useState(addDays(todayISO(), 30));
  const [notes, setNotes] = useState('');
  const [lineItems, setLineItems] = useState<LineItemRow[]>([]);
  const [errors, setErrors] = useState<string[]>([]);

  // Recurring state
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringSchedule, setRecurringSchedule] = useState<'MONTHLY' | 'QUARTERLY' | 'ANNUALLY'>('MONTHLY');
  const [recurringStartDate, setRecurringStartDate] = useState(todayISO());
  const [recurringEndDate, setRecurringEndDate] = useState('');

  // Fetch client's pricing config
  const { data: pricingData, loading: pricingLoading } = useQuery<
    GetOrganizationPricingResponse,
    GetOrganizationPricingVariables
  >(GET_ORGANIZATION_PRICING, {
    variables: { organizationId: clientId },
    skip: !isOpen || !clientId,
  });

  // Build product catalog from pricing data
  const productCatalog = useMemo(() => {
    if (!pricingData?.organizationPricing) return [];

    const items: Array<{
      productId: string;
      sku: string;
      name: string;
      description: string;
      effectivePrice: number;
      priceSource: 'override' | 'catalog' | 'custom';
      catalogPrice: number | null;
      overridePrice: number | null;
      type: 'software' | 'custom';
    }> = [];

    // Software SKUs (org overrides + catalog defaults)
    for (const sw of pricingData.organizationPricing.softwareSkus) {
      const hasOverride = sw.overridePrice !== null && sw.overridePrice !== undefined;
      items.push({
        productId: sw.productId,
        sku: sw.product.sku,
        name: sw.product.name,
        description: sw.product.description || sw.product.name,
        effectivePrice: hasOverride ? sw.overridePrice! : sw.product.unitPrice,
        priceSource: hasOverride ? 'override' : 'catalog',
        catalogPrice: sw.product.unitPrice,
        overridePrice: hasOverride ? sw.overridePrice : null,
        type: 'software',
      });
    }

    // Custom SKUs
    for (const cs of pricingData.organizationPricing.customSkus) {
      if (!cs.isActive) continue;
      items.push({
        productId: cs.id, // custom SKU id used as productId
        sku: cs.sku,
        name: cs.name,
        description: cs.description || cs.name,
        effectivePrice: cs.price,
        priceSource: 'custom',
        catalogPrice: null,
        overridePrice: null,
        type: 'custom',
      });
    }

    return items;
  }, [pricingData]);

  // Mutations
  const [createInvoice, { loading: creating }] = useMutation(CREATE_INVOICE, {
    refetchQueries: [
      { query: GET_INVOICES_BY_ORGANIZATION, variables: { organizationId: clientId } },
    ],
  });

  const [approveInvoice, { loading: approving }] = useMutation(APPROVE_INVOICE);

  const isSubmitting = creating || approving;

  // Reset form when opened
  useEffect(() => {
    if (isOpen) {
      setInvoiceDate(todayISO());
      setDueDate(addDays(todayISO(), 30));
      setNotes('');
      setLineItems([]);
      setErrors([]);
      setIsRecurring(false);
      setRecurringSchedule('MONTHLY');
      setRecurringStartDate(todayISO());
      setRecurringEndDate('');
    }
  }, [isOpen]);

  // Auto-update due date when invoice date changes
  useEffect(() => {
    if (invoiceDate) {
      setDueDate(addDays(invoiceDate, 30));
    }
  }, [invoiceDate]);

  // ==================== Line Item Handlers ====================

  function addLineItem() {
    setLineItems((prev) => [
      ...prev,
      {
        id: nextTempId(),
        productId: '',
        sku: '',
        description: '',
        unitPrice: 0,
        priceSource: '',
        quantity: 1,
        lineTotal: 0,
      },
    ]);
  }

  function removeLineItem(id: string) {
    setLineItems((prev) => prev.filter((li) => li.id !== id));
  }

  function updateLineItem(id: string, updates: Partial<LineItemRow>) {
    setLineItems((prev) =>
      prev.map((li) => {
        if (li.id !== id) return li;
        const updated = { ...li, ...updates };
        updated.lineTotal = updated.unitPrice * updated.quantity;
        return updated;
      })
    );
  }

  function handleProductSelect(lineItemId: string, productId: string) {
    const product = productCatalog.find((p) => p.productId === productId);
    if (!product) return;

    updateLineItem(lineItemId, {
      productId: product.productId,
      sku: product.sku,
      description: product.description,
      unitPrice: product.effectivePrice,
      priceSource: product.priceSource,
    });
  }

  // ==================== Subtotal ====================

  const subtotal = lineItems.reduce((sum, li) => sum + li.lineTotal, 0);

  // ==================== Validation ====================

  function validate(): string[] {
    const errs: string[] = [];
    if (!invoiceDate) errs.push('Invoice date is required.');
    if (!dueDate) errs.push('Due date is required.');
    if (lineItems.length === 0) errs.push('At least one line item is required.');
    for (const li of lineItems) {
      if (!li.productId) errs.push(`Line item "${li.sku || '(empty)'}" needs a product selected.`);
      if (li.quantity < 1) errs.push(`Quantity must be at least 1 for "${li.sku}".`);
      if (li.unitPrice <= 0) errs.push(`Unit price must be positive for "${li.sku}".`);
    }
    return errs;
  }

  // ==================== Submit ====================

  async function handleSubmit(andApprove: boolean) {
    const errs = validate();
    if (errs.length > 0) {
      setErrors(errs);
      return;
    }
    setErrors([]);

    try {
      const input: Record<string, unknown> = {
        organizationId: clientId,
        invoiceDate,
        dueDate,
        notes: notes.trim() || undefined,
        lineItems: lineItems.map((li) => ({
          productId: li.productId,
          quantity: li.quantity,
          unitPriceOverride: li.priceSource === 'override' || li.priceSource === 'custom'
            ? undefined // server will resolve from org pricing
            : undefined,
        })),
      };

      // Add recurring fields if enabled
      if (isRecurring) {
        input.isRecurring = true;
        input.recurringSchedule = recurringSchedule;
        input.recurringStartDate = recurringStartDate;
        input.recurringEndDate = recurringEndDate || undefined;
      }

      const { data } = await createInvoice({ variables: { input } });
      const newInvoice: Invoice = data.createInvoice;

      if (andApprove && newInvoice) {
        await approveInvoice({
          variables: { id: newInvoice.id },
          refetchQueries: [
            { query: GET_INVOICES_BY_ORGANIZATION, variables: { organizationId: clientId } },
          ],
        });
      }

      onClose();
    } catch (err: unknown) {
      setErrors([err instanceof Error ? err.message : 'Failed to create invoice.']);
    }
  }

  // ==================== Render ====================

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Create Invoice"
      size="xl"
      footer={
        <>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="btn-ghost px-4 py-2 text-sm"
          >
            Cancel
          </button>
          <button
            onClick={() => handleSubmit(false)}
            disabled={isSubmitting}
            className="btn-ghost px-4 py-2 text-sm border border-gray dark:border-night-pond"
          >
            {creating && !approving ? (
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin inline" />
            ) : null}
            Save as Draft
          </button>
          <button
            onClick={() => handleSubmit(true)}
            disabled={isSubmitting}
            className="btn-primary px-4 py-2 text-sm"
          >
            {approving ? (
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin inline" />
            ) : null}
            Create & Approve
          </button>
        </>
      }
    >
      <div className="space-y-6">
        {/* Errors */}
        {errors.length > 0 && (
          <div className="bg-rust/10 dark:bg-salmon/10 border border-rust/20 dark:border-salmon/20 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-rust dark:text-salmon mt-0.5 flex-shrink-0" />
              <ul className="text-sm text-rust dark:text-salmon space-y-0.5">
                {errors.map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Dates */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate dark:text-mist mb-1.5">
              Invoice Date <span className="text-rust">*</span>
            </label>
            <input
              type="date"
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value)}
              className="input w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate dark:text-mist mb-1.5">
              Due Date <span className="text-rust">*</span>
            </label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="input w-full"
            />
            <p className="text-xs text-chalkboard/60 dark:text-rain-cloud/60 mt-1">
              Net 30 from invoice date
            </p>
          </div>
        </div>

        {/* Line Items */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium text-slate dark:text-mist">
              Line Items <span className="text-rust">*</span>
            </label>
            <button
              onClick={addLineItem}
              disabled={pricingLoading}
              className="btn-ghost text-xs px-2.5 py-1.5 flex items-center gap-1.5 text-pond dark:text-pond-light"
            >
              <Plus className="w-3.5 h-3.5" />
              Add Item
            </button>
          </div>

          {pricingLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-pond dark:text-pond-light" />
              <span className="ml-2 text-sm text-chalkboard dark:text-rain-cloud">
                Loading product catalog...
              </span>
            </div>
          ) : lineItems.length === 0 ? (
            <div className="border border-dashed border-gray dark:border-night-pond rounded-lg p-6 text-center">
              <p className="text-sm text-chalkboard dark:text-rain-cloud mb-2">
                No line items added yet.
              </p>
              <button
                onClick={addLineItem}
                className="btn-ghost text-sm px-3 py-1.5 text-pond dark:text-pond-light border border-pond/20 dark:border-pond-light/20"
              >
                <Plus className="w-4 h-4 mr-1.5 inline" />
                Add First Item
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Header */}
              <div className="grid grid-cols-12 gap-2 text-xs font-medium text-chalkboard dark:text-rain-cloud px-1">
                <div className="col-span-4">Product</div>
                <div className="col-span-2">SKU</div>
                <div className="col-span-2 text-right">Unit Price</div>
                <div className="col-span-1 text-center">Qty</div>
                <div className="col-span-2 text-right">Line Total</div>
                <div className="col-span-1" />
              </div>

              {/* Rows */}
              {lineItems.map((li) => {
                const catalogItem = productCatalog.find((p) => p.productId === li.productId);
                return (
                  <div
                    key={li.id}
                    className="grid grid-cols-12 gap-2 items-start bg-pale-gray dark:bg-puddle/20 rounded-lg p-2"
                  >
                    {/* Product Select */}
                    <div className="col-span-4">
                      <select
                        value={li.productId}
                        onChange={(e) => handleProductSelect(li.id, e.target.value)}
                        className="select w-full text-sm py-1.5"
                      >
                        <option value="">Select product...</option>
                        {productCatalog.length > 0 && (
                          <>
                            <optgroup label="Software SKUs">
                              {productCatalog
                                .filter((p) => p.type === 'software')
                                .map((p) => (
                                  <option key={p.productId} value={p.productId}>
                                    {p.name}
                                  </option>
                                ))}
                            </optgroup>
                            {productCatalog.some((p) => p.type === 'custom') && (
                              <optgroup label="Custom SKUs">
                                {productCatalog
                                  .filter((p) => p.type === 'custom')
                                  .map((p) => (
                                    <option key={p.productId} value={p.productId}>
                                      {p.name}
                                    </option>
                                  ))}
                              </optgroup>
                            )}
                          </>
                        )}
                      </select>
                    </div>

                    {/* SKU */}
                    <div className="col-span-2 flex items-center">
                      <span className="text-sm text-chalkboard dark:text-rain-cloud font-mono">
                        {li.sku || '—'}
                      </span>
                    </div>

                    {/* Unit Price */}
                    <div className="col-span-2">
                      <div className="relative">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={li.unitPrice || ''}
                          onChange={(e) =>
                            updateLineItem(li.id, {
                              unitPrice: parseFloat(e.target.value) || 0,
                            })
                          }
                          className="input w-full text-sm text-right py-1.5 pr-2"
                        />
                      </div>
                      {catalogItem && (
                        <div className="flex items-center gap-1 mt-0.5">
                          <Info className="w-3 h-3 text-chalkboard/40 dark:text-rain-cloud/40" />
                          <span className="text-[10px] text-chalkboard/60 dark:text-rain-cloud/60">
                            {catalogItem.priceSource === 'override'
                              ? 'Client-specific price'
                              : catalogItem.priceSource === 'custom'
                                ? 'Custom SKU price'
                                : 'Standard catalog price'}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Quantity */}
                    <div className="col-span-1">
                      <input
                        type="number"
                        min="1"
                        value={li.quantity}
                        onChange={(e) =>
                          updateLineItem(li.id, {
                            quantity: parseInt(e.target.value, 10) || 1,
                          })
                        }
                        className="input w-full text-sm text-center py-1.5"
                      />
                    </div>

                    {/* Line Total */}
                    <div className="col-span-2 flex items-center justify-end">
                      <span className="text-sm font-medium text-slate dark:text-mist">
                        {formatCurrency(li.lineTotal)}
                      </span>
                    </div>

                    {/* Remove */}
                    <div className="col-span-1 flex items-center justify-center">
                      <button
                        onClick={() => removeLineItem(li.id)}
                        className="p-1 rounded text-chalkboard/40 hover:text-rust dark:text-rain-cloud/40 dark:hover:text-salmon transition-colors"
                        title="Remove line item"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}

              {/* Subtotal */}
              <div className="flex justify-end items-center gap-4 pt-2 pr-12 border-t border-gray dark:border-night-pond">
                <span className="text-sm font-medium text-chalkboard dark:text-rain-cloud">
                  Subtotal
                </span>
                <span className="text-lg font-semibold text-slate dark:text-mist">
                  {formatCurrency(subtotal)}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Recurring Invoice Toggle */}
        <div className="border border-gray dark:border-night-pond rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <RefreshCw className="w-4 h-4 text-violet dark:text-violet-light" />
              <label className="text-sm font-medium text-slate dark:text-mist">
                Make Recurring
              </label>
            </div>
            <button
              type="button"
              onClick={() => setIsRecurring(!isRecurring)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                isRecurring
                  ? 'bg-pond dark:bg-pond-light'
                  : 'bg-gray dark:bg-night-pond'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  isRecurring ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {isRecurring && (
            <div className="space-y-3 pt-1">
              <p className="text-xs text-chalkboard/60 dark:text-rain-cloud/60">
                This invoice will serve as a template. New invoices will be auto-generated as <strong>drafts</strong> on the configured schedule.
              </p>

              {/* Schedule */}
              <div>
                <label className="block text-xs font-medium text-chalkboard dark:text-rain-cloud mb-1">
                  Frequency <span className="text-rust">*</span>
                </label>
                <select
                  value={recurringSchedule}
                  onChange={(e) => setRecurringSchedule(e.target.value as 'MONTHLY' | 'QUARTERLY' | 'ANNUALLY')}
                  className="select w-full text-sm py-1.5"
                >
                  <option value="MONTHLY">Monthly</option>
                  <option value="QUARTERLY">Quarterly (every 3 months)</option>
                  <option value="ANNUALLY">Annually</option>
                </select>
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-chalkboard dark:text-rain-cloud mb-1">
                    Start Date <span className="text-rust">*</span>
                  </label>
                  <input
                    type="date"
                    value={recurringStartDate}
                    onChange={(e) => setRecurringStartDate(e.target.value)}
                    className="input w-full text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-chalkboard dark:text-rain-cloud mb-1">
                    End Date <span className="text-chalkboard/40 dark:text-rain-cloud/40">(optional)</span>
                  </label>
                  <input
                    type="date"
                    value={recurringEndDate}
                    onChange={(e) => setRecurringEndDate(e.target.value)}
                    min={recurringStartDate}
                    className="input w-full text-sm"
                  />
                  <p className="text-[10px] text-chalkboard/50 dark:text-rain-cloud/50 mt-0.5">
                    Leave empty for indefinite
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-slate dark:text-mist mb-1.5">
            Notes
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Optional notes for this invoice..."
            className="textarea w-full"
          />
        </div>
      </div>
    </Modal>
  );
}
